/**
 * 工作流模板系统
 * 提供预定义的工作流模板
 */

import { WorkflowTemplate, WorkflowDefinitionV2 } from '../types/workflow-v2';
import fs from 'fs-extra';
import path from 'path';
import { logger } from '../utils/logger';

export class WorkflowTemplateManager {
  private templatesDir: string;
  private builtinTemplates: Map<string, WorkflowTemplate> = new Map();
  
  constructor(workflowDir?: string) {
    const projectRoot = workflowDir || process.cwd();
    this.templatesDir = path.join(projectRoot, '.workflow', 'templates', 'workflows');
    this.loadBuiltinTemplates();
  }
  
  /**
   * 加载内置模板
   */
  private loadBuiltinTemplates() {
    // CI/CD 模板
    this.builtinTemplates.set('ci-cd', {
      id: 'ci-cd',
      name: 'CI/CD Pipeline',
      description: 'Complete CI/CD pipeline with testing and deployment',
      category: 'DevOps',
      tags: ['ci', 'cd', 'testing', 'deployment'],
      definition: {
        id: 'ci-cd-pipeline',
        version: '2.0',
        name: 'CI/CD Pipeline',
        description: 'Automated build, test, and deploy pipeline',
        triggers: [{
          id: 'git-push',
          type: 'git',
          enabled: true,
          git: {
            events: ['push'],
            branches: ['main', 'develop'],
          },
        }],
        nodes: [
          {
            id: 'checkout',
            name: 'Checkout Code',
            type: 'action',
            action: {
              type: 'command',
              command: 'git checkout ${inputs.branch}',
            },
            onSuccess: ['install'],
          },
          {
            id: 'install',
            name: 'Install Dependencies',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm install',
            },
            onSuccess: ['lint', 'test'],
          },
          {
            id: 'lint',
            name: 'Run Linting',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run lint',
            },
          },
          {
            id: 'test',
            name: 'Run Tests',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run test',
            },
            onSuccess: ['build'],
          },
          {
            id: 'build',
            name: 'Build Project',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run build',
            },
            onSuccess: ['deploy-check'],
          },
          {
            id: 'deploy-check',
            name: 'Check Deploy Condition',
            type: 'condition',
            condition: {
              field: 'inputs.branch',
              operator: 'equals',
              value: 'main',
            },
            branches: {
              true: ['deploy'],
              false: ['notify-success'],
            },
          },
          {
            id: 'deploy',
            name: 'Deploy to Production',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run deploy:prod',
            },
            onSuccess: ['notify-success'],
            onFailure: ['notify-failure'],
          },
          {
            id: 'notify-success',
            name: 'Notify Success',
            type: 'action',
            action: {
              type: 'api',
              url: '${env.WEBHOOK_URL}',
              method: 'POST',
              body: {
                status: 'success',
                project: '${inputs.project}',
                branch: '${inputs.branch}',
              },
            },
          },
          {
            id: 'notify-failure',
            name: 'Notify Failure',
            type: 'action',
            action: {
              type: 'api',
              url: '${env.WEBHOOK_URL}',
              method: 'POST',
              body: {
                status: 'failure',
                project: '${inputs.project}',
                branch: '${inputs.branch}',
                error: '${nodes.deploy.error}',
              },
            },
          },
        ],
        entryNodes: ['checkout'],
        inputs: {
          branch: {
            type: 'string',
            required: true,
            default: 'main',
            description: 'Git branch to build',
          },
          project: {
            type: 'string',
            required: true,
            description: 'Project name',
          },
        },
      },
      metadata: {
        author: 'Gongfu Team',
        version: '1.0.0',
        created: new Date(),
      },
    });
    
    // 测试自动化模板
    this.builtinTemplates.set('test-automation', {
      id: 'test-automation',
      name: 'Test Automation',
      description: 'Automated testing workflow with parallel execution',
      category: 'Testing',
      tags: ['testing', 'automation', 'quality'],
      definition: {
        id: 'test-automation',
        version: '2.0',
        name: 'Automated Testing',
        triggers: [{
          id: 'schedule',
          type: 'time',
          enabled: true,
          schedule: {
            cron: '0 2 * * *', // 每天凌晨2点
          },
        }],
        nodes: [
          {
            id: 'prepare',
            name: 'Prepare Test Environment',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run test:prepare',
            },
            onSuccess: ['test-parallel'],
          },
          {
            id: 'test-parallel',
            name: 'Run Tests in Parallel',
            type: 'parallel',
            parallel: {
              branches: [
                ['unit-tests'],
                ['integration-tests'],
                ['e2e-tests'],
              ],
              waitAll: true,
            },
            onSuccess: ['generate-report'],
          },
          {
            id: 'unit-tests',
            name: 'Unit Tests',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run test:unit',
            },
          },
          {
            id: 'integration-tests',
            name: 'Integration Tests',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run test:integration',
            },
          },
          {
            id: 'e2e-tests',
            name: 'E2E Tests',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run test:e2e',
            },
          },
          {
            id: 'generate-report',
            name: 'Generate Test Report',
            type: 'action',
            action: {
              type: 'command',
              command: 'pnpm run test:report',
            },
          },
        ],
        entryNodes: ['prepare'],
      },
      metadata: {
        author: 'Gongfu Team',
        version: '1.0.0',
        created: new Date(),
      },
    });
    
    // PRD 到任务模板
    this.builtinTemplates.set('prd-to-tasks', {
      id: 'prd-to-tasks',
      name: 'PRD to Tasks',
      description: 'Generate tasks from PRD with AI enhancement',
      category: 'Planning',
      tags: ['prd', 'tasks', 'ai', 'planning'],
      definition: {
        id: 'prd-to-tasks',
        version: '2.0',
        name: 'PRD to Tasks Workflow',
        triggers: [{
          id: 'prd-created',
          type: 'event',
          enabled: true,
          event: {
            name: 'prd:created',
          },
        }],
        nodes: [
          {
            id: 'analyze-prd',
            name: 'Analyze PRD',
            type: 'action',
            action: {
              type: 'agent',
              agentId: 'agent-product',
              prompt: 'Analyze the PRD and identify key features and requirements: ${inputs.prdContent}',
            },
            onSuccess: ['generate-design'],
          },
          {
            id: 'generate-design',
            name: 'Generate Design Document',
            type: 'task',
            task: {
              content: 'Create system design for ${inputs.prdTitle}',
              type: 'design',
              priority: 'high',
              estimate: '2d',
            },
            onSuccess: ['create-tasks'],
          },
          {
            id: 'create-tasks',
            name: 'Create Development Tasks',
            type: 'loop',
            loop: {
              type: 'foreach',
              items: 'nodes.analyze-prd.result.features',
              itemName: 'feature',
            },
            loopBody: ['create-feature-task'],
            onSuccess: ['assign-agents'],
          },
          {
            id: 'create-feature-task',
            name: 'Create Feature Task',
            type: 'task',
            task: {
              content: 'Implement ${feature.name}',
              type: 'development',
              priority: '${feature.priority}',
              estimate: '${feature.estimate}',
              tags: ['${feature.module}'],
            },
          },
          {
            id: 'assign-agents',
            name: 'Assign Agents to Tasks',
            type: 'action',
            action: {
              type: 'script',
              language: 'javascript',
              script: `
                const tasks = Object.values(context.tasks);
                for (const task of tasks) {
                  // AI-powered agent assignment
                  console.log('Assigning agent to task:', task.id);
                }
              `,
            },
          },
        ],
        entryNodes: ['analyze-prd'],
        inputs: {
          prdId: {
            type: 'string',
            required: true,
            description: 'PRD ID',
          },
          prdTitle: {
            type: 'string',
            required: true,
            description: 'PRD Title',
          },
          prdContent: {
            type: 'string',
            required: true,
            description: 'PRD Content',
          },
        },
      },
      metadata: {
        author: 'Gongfu Team',
        version: '1.0.0',
        created: new Date(),
      },
    });
    
    // 代码审查工作流
    this.builtinTemplates.set('code-review', {
      id: 'code-review',
      name: 'Code Review Workflow',
      description: 'Automated code review with multiple reviewers',
      category: 'Quality',
      tags: ['review', 'quality', 'collaboration'],
      definition: {
        id: 'code-review',
        version: '2.0',
        name: 'Code Review Process',
        triggers: [{
          id: 'pr-opened',
          type: 'git',
          enabled: true,
          git: {
            events: ['pull_request'],
          },
        }],
        nodes: [
          {
            id: 'analyze-changes',
            name: 'Analyze Code Changes',
            type: 'action',
            action: {
              type: 'agent',
              agentId: 'agent-reviewer',
              prompt: 'Analyze the code changes in PR #${inputs.prNumber}',
            },
            onSuccess: ['check-complexity'],
          },
          {
            id: 'check-complexity',
            name: 'Check Code Complexity',
            type: 'condition',
            condition: {
              field: 'nodes.analyze-changes.result.complexity',
              operator: 'greater_than',
              value: 50,
            },
            branches: {
              true: ['request-senior-review'],
              false: ['standard-review'],
            },
          },
          {
            id: 'standard-review',
            name: 'Standard Review',
            type: 'approval',
            approval: {
              approvers: ['${inputs.reviewer}'],
              message: 'Please review PR #${inputs.prNumber}',
              timeout: 86400000, // 24 hours
            },
            onSuccess: ['merge-pr'],
          },
          {
            id: 'request-senior-review',
            name: 'Request Senior Review',
            type: 'approval',
            approval: {
              approvers: ['${inputs.seniorReviewer}', '${inputs.reviewer}'],
              requireAll: true,
              message: 'Complex changes in PR #${inputs.prNumber} require senior review',
              timeout: 172800000, // 48 hours
            },
            onSuccess: ['merge-pr'],
          },
          {
            id: 'merge-pr',
            name: 'Merge Pull Request',
            type: 'action',
            action: {
              type: 'command',
              command: 'gh pr merge ${inputs.prNumber} --squash',
            },
          },
        ],
        entryNodes: ['analyze-changes'],
        inputs: {
          prNumber: {
            type: 'number',
            required: true,
            description: 'Pull Request number',
          },
          reviewer: {
            type: 'string',
            required: true,
            description: 'Primary reviewer',
          },
          seniorReviewer: {
            type: 'string',
            required: true,
            description: 'Senior reviewer for complex changes',
          },
        },
      },
      metadata: {
        author: 'Gongfu Team',
        version: '1.0.0',
        created: new Date(),
      },
    });
  }
  
  /**
   * 获取所有模板
   */
  async getAllTemplates(): Promise<WorkflowTemplate[]> {
    const templates: WorkflowTemplate[] = Array.from(this.builtinTemplates.values());
    
    // 加载自定义模板
    if (await fs.pathExists(this.templatesDir)) {
      const files = await fs.readdir(this.templatesDir);
      
      for (const file of files) {
        if (file.endsWith('.json')) {
          try {
            const content = await fs.readJSON(path.join(this.templatesDir, file));
            templates.push(content);
          } catch (error) {
            logger.error(`Failed to load template ${file}:`, error);
          }
        }
      }
    }
    
    return templates;
  }
  
  /**
   * 获取模板
   */
  async getTemplate(id: string): Promise<WorkflowTemplate | null> {
    // 检查内置模板
    if (this.builtinTemplates.has(id)) {
      return this.builtinTemplates.get(id)!;
    }
    
    // 检查自定义模板
    const templatePath = path.join(this.templatesDir, `${id}.json`);
    if (await fs.pathExists(templatePath)) {
      return await fs.readJSON(templatePath);
    }
    
    return null;
  }
  
  /**
   * 保存自定义模板
   */
  async saveTemplate(template: WorkflowTemplate): Promise<void> {
    await fs.ensureDir(this.templatesDir);
    const templatePath = path.join(this.templatesDir, `${template.id}.json`);
    await fs.writeJSON(templatePath, template, { spaces: 2 });
    logger.info(`Template saved: ${template.id}`);
  }
  
  /**
   * 从工作流创建模板
   */
  createTemplateFromWorkflow(
    workflow: WorkflowDefinitionV2,
    metadata: {
      name: string;
      description: string;
      category: string;
      tags: string[];
    }
  ): WorkflowTemplate {
    return {
      id: `template-${Date.now()}`,
      name: metadata.name,
      description: metadata.description,
      category: metadata.category,
      tags: metadata.tags,
      definition: workflow,
      metadata: {
        author: process.env.USER || 'unknown',
        version: '1.0.0',
        created: new Date(),
      },
    };
  }
  
  /**
   * 从模板创建工作流
   */
  async createWorkflowFromTemplate(
    templateId: string,
    inputs: Record<string, any> = {}
  ): Promise<WorkflowDefinitionV2> {
    const template = await this.getTemplate(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    // 深拷贝定义
    const workflow: WorkflowDefinitionV2 = JSON.parse(JSON.stringify(template.definition));
    
    // 生成新 ID
    workflow.id = `workflow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // 应用输入参数
    if (workflow.variables) {
      workflow.variables = { ...workflow.variables, ...inputs };
    }
    
    return workflow;
  }
  
  /**
   * 搜索模板
   */
  async searchTemplates(query: {
    category?: string;
    tags?: string[];
    keyword?: string;
  }): Promise<WorkflowTemplate[]> {
    const allTemplates = await this.getAllTemplates();
    
    return allTemplates.filter(template => {
      if (query.category && template.category !== query.category) {
        return false;
      }
      
      if (query.tags && query.tags.length > 0) {
        const hasAllTags = query.tags.every(tag => template.tags.includes(tag));
        if (!hasAllTags) return false;
      }
      
      if (query.keyword) {
        const keyword = query.keyword.toLowerCase();
        const inName = template.name.toLowerCase().includes(keyword);
        const inDesc = template.description.toLowerCase().includes(keyword);
        const inTags = template.tags.some(tag => tag.toLowerCase().includes(keyword));
        if (!inName && !inDesc && !inTags) return false;
      }
      
      return true;
    });
  }
}